import {
  TeButtonMinimal,
  TeContainer,
  TeDivider,
  TeGrid,
  TeKpi,
  TeText,
  TeCallout,
} from '@topcon/tuicl-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlaceholderContainer } from '@topcon/topcon-utils';

import { Section } from '../../utils/styling';
import { AppRoutes } from '../../app/routes/utils';
import { InviteToJoinBody, RequestToJoinBody, UserBody } from '../../api/generated/pap';

const CalloutWrapper = styled.div`
  position: relative;
  width: 100%;
`;
const CalloutAction = styled.div`
  position: absolute;
  right: 48px;
  top: 50%;
  transform: translateY(-50%);
`;

interface Props {
  users: UserBody[] | undefined;
  invitations: InviteToJoinBody[] | undefined;
  requests: RequestToJoinBody[] | undefined;
  showReviewUsersCallout: boolean;
  handleCloseCallout?: () => void;
  onReviewSuggestedUsers?: () => void;
}

function UsersKPI({
  users,
  invitations,
  requests,
  showReviewUsersCallout,
  handleCloseCallout,
  onReviewSuggestedUsers,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeUsersCount = users?.length || 0;
  const invitationsCount = invitations?.length || 0;
  const requestsCount = requests?.length || 0;
  const totalUsersCount = activeUsersCount + invitationsCount + requestsCount;

  return (
    <Section>
      <TeContainer alignItems="center" justifyContent="space-between" fill gap="xs">
        <TeText size={16} weight={700}>
          {t('users')}
        </TeText>
        <TeButtonMinimal
          text={t('manageUsers')}
          size="s"
          postfixIcon="arrow_forward"
          teclick={() => navigate(AppRoutes.Users)}
          virtualHref={AppRoutes.Users}
        />
      </TeContainer>

      <TeDivider dividerColor="border.secondary" />

      {showReviewUsersCallout && (
        <TeContainer fill style={{ padding: '16px', paddingBottom: 0 }}>
          <CalloutWrapper>
            <TeCallout
              status="info"
              icon="info"
              heading={t('suggestedUsers.heading')}
              text={t('suggestedUsers.description')}
              closeable
              teclose={handleCloseCallout}
              grow
              style={{ borderColor: '#005FAD' }}
            >
              <CalloutAction>
                <TeButtonMinimal
                  text={t('suggestedUsers.reviewSuggestions')}
                  size="s"
                  teclick={onReviewSuggestedUsers}
                />
              </CalloutAction>
            </TeCallout>
          </CalloutWrapper>
        </TeContainer>
      )}

      {totalUsersCount ? (
        <TeGrid gap="s">
          <TeKpi heading={`${totalUsersCount}`} description={t('totalUsers')} grow />
          <TeKpi
            heading={`${activeUsersCount}`}
            description={t('activeUsers')}
            status="success"
            grow
          />
          <TeKpi
            heading={`${invitationsCount}`}
            description={t('invitedUsers')}
            status="info"
            grow
          />
          <TeKpi heading={`${requestsCount}`} description={t('requests')} status="info" grow />
        </TeGrid>
      ) : (
        <TeContainer fill>
          <PlaceholderContainer
            grow
            icon="groups"
            text={t('noUsers')}
            backgroundColor="background.secondary"
          />
        </TeContainer>
      )}
    </Section>
  );
}

export default UsersKPI;
